'use client';
import { useState, useEffect, useCallback } from 'react';
import {
  useProduct,
  useSelectedVariant,
  useAdditionalProducts
} from '@/components/product/product-context';
import { useCampaign } from '@/app/CampaignContext';
import Price from '@/components/price/Price';
import { useModal } from '@/components/modal/ModalProvider';

export default function HeaderSticky() {
  const { productData } = useProduct();
  const { variant } = useSelectedVariant();
  const { additionalProducts } = useAdditionalProducts();
  const campaignContext = useCampaign();
  const pricingDetailsData = campaignContext?.pricing_details;
  const productOutOfStock = productData?.shopifyProduct?.outOfStock;
  const variantOutOfStock = variant?.outOfStock;
  const [showSticky, setShowSticky] = useState(false);
  const [scrollDown, setScrollDown] = useState(false);
  const [cachedScrollY, setCachedScrollY] = useState(0);
  const [cachedCtaPosition, setCachedCtaPosition] = useState(0);
  const { showPriceDetails } = useModal();

  // button states
  const getButtonCopy = (numItems) => {
    if (numItems > 1) {
      return `Add ${numItems} items to cart`;
    }
    return 'Add to cart';
  };

  const numItems = additionalProducts.length + 1;
  const initialText = getButtonCopy(numItems);
  const initialButtonColor = 'opacity-100';
  const [buttonText, setButtonText] = useState(initialText);
  const [buttonColor, setButtonColor] = useState(initialButtonColor);

  // Update button text when additionalProducts changes
  useEffect(() => {
    const newNumItems = additionalProducts.length + 1;
    const newButtonText = getButtonCopy(newNumItems);
    setButtonText(newButtonText);
  }, [additionalProducts]);

  function handleClick() {
    //button doesn't actually run any of the ATC logic by itself, it instead finds the main ATC button and clicks it
    document?.getElementById('AddToCart')?.click();

    //set button color and text states while it's adding, not actually hooked into cart events, just puts an arbitrary timeout to fake it waiting
    setButtonColor('opacity-75');
    setButtonText('Adding');
    setTimeout(() => {
      const newNumItems = additionalProducts.length + 1;
      setButtonText(getButtonCopy(newNumItems));
      setButtonColor(initialButtonColor);
    }, 2000);
  }

  const renderPriceDetails = (product, className = '') => {
    return (
      pricingDetailsData && (
        <button
          type="button"
          className={`cursor-pointer pb-1 text-center underline ${className}`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();

            // Immediately freeze the sticky state to prevent jiggling
            const currentScrollY = window.scrollY;
            const ctaElement = document.querySelector('#AddToCart');
            let currentCtaPosition = 0;

            if (ctaElement) {
              currentCtaPosition =
                ctaElement.getBoundingClientRect().top + currentScrollY - 10;
            }

            // Cache values and freeze sticky state
            setCachedScrollY(currentScrollY);
            setCachedCtaPosition(currentCtaPosition);

            // Immediately set sticky state based on current position to prevent flicker
            setShowSticky(currentScrollY >= currentCtaPosition);

            // Add a small delay to ensure any existing scroll operations complete
            requestAnimationFrame(() => {
              showPriceDetails(
                `header-sticky-${product}`,
                null,
                pricingDetailsData
              );
            });
          }}
          aria-label={`View price details for ${product}`}
          aria-haspopup="dialog"
        >
          Price details
        </button>
      )
    );
  };

  //logic for sticky is that it should show if user scrolls past the main ATC button.
  const scrollListener = useCallback(() => {
    // If modal is open, use cached values to prevent CLS from scrollbar changes
    if (document.body.classList.contains('modal-open')) {
      // Use cached scroll position to maintain sticky state
      setShowSticky(cachedScrollY >= cachedCtaPosition);
      return;
    }

    var ctaPosition =
      document.querySelector('#AddToCart')?.getBoundingClientRect().top +
      window.scrollY -
      10;
    if (window.scrollY >= ctaPosition) {
      setShowSticky(true);
    } else {
      setShowSticky(false);
    }
  }, [cachedScrollY, cachedCtaPosition]);

  useEffect(() => {
    window.addEventListener('scroll', scrollListener);

    return () => window.removeEventListener('scroll', scrollListener);
  }, [scrollListener]);

  //If user is scrolling down, show the sticky at the top. If user is scrolling up, show the nav bar and then the sticky.
  useEffect(() => {
    const threshold = 0;
    let prevScrollY = window.scrollY;
    let ticking = false;

    const updateScrollDown = () => {
      // If modal is open, use cached scroll position to prevent CLS
      if (document.body.classList.contains('modal-open')) {
        // Use cached values to determine scroll direction
        setScrollDown(cachedScrollY > prevScrollY ? false : true);
        ticking = false;
        return;
      }

      const scrollY = window.scrollY;

      if (Math.abs(scrollY - prevScrollY) < threshold) {
        ticking = false;
        return;
      }
      setScrollDown(scrollY > prevScrollY ? false : true);

      prevScrollY = scrollY > 0 ? scrollY : 0;
      ticking = false;
    };

    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(updateScrollDown);
        ticking = true;
      }
    };

    window.addEventListener('scroll', onScroll);

    return () => window.removeEventListener('scroll', onScroll);
  }, [scrollDown, cachedScrollY]);

  //callout that the sticky has some unique breakpoints, this is to handle longer title products. So we keep things like smaller fonts past the normal tablet breakpoints in order to give enough space.
  if (variant && showSticky) {
    return (
      <section
        id="header-sticky"
        data-name="header-sticky"
        className={`fixed ${
          scrollDown == true ? 'top-[58px] z-40' : 'top-0 z-50'
        } border-dawn-grey-30 left-0 w-full border-b bg-white text-left`}
      >
        <div className="flex w-full items-center justify-between px-3 py-2 text-left min-[840px]:px-10 min-[1133px]:px-20">
          <div className="text-left max-[840px]:min-w-[49%]">
            <h2 className="h6 min-[840px]:h5 min-[1133px]:h4 mr-5 block truncate text-left text-black">
              {variant.productTitle}
            </h2>
            <div className="inline-block min-[840px]:hidden">
              <h3 className="price-s-label sm:price-r-label lg:price-l-label inline-block text-black">
                {variant.variantTitle}:{' '}
              </h3>
              <span className="inline-block">
                <Price
                  price={variant.price}
                  compareAtPrice={variant.compareAtPrice}
                  priceClassOverride={`ml-1 price-s lg:price-l`}
                  crossedPriceClassOverride={`price-s-crossed lg:price-l-crossed`}
                />
              </span>
              {renderPriceDetails(
                variant.productTitle,
                'body-xs inline-block cursor-pointer pl-1 text-grey-text underline'
              )}
            </div>
          </div>
          <div className="flex items-center">
            <div className="hidden items-center whitespace-nowrap min-[840px]:flex">
              <h3 className="sm:price-r-label lg:price-l-label self-center text-center text-black">
                {variant.variantTitle}:{' '}
              </h3>
              <Price
                price={variant.price}
                compareAtPrice={variant.compareAtPrice}
                priceClassOverride={`ml-1 sm:price-r min-[70.8125rem]:price-l`}
                crossedPriceClassOverride={`price-l-crossed sm:price-r-crossed min-[70.8125rem]:price-l-crossed`}
              />
              {renderPriceDetails(
                variant.productTitle,
                'body-xs inline-block cursor-pointer pl-2 text-grey-text underline pb-0!'
              )}
            </div>

            {productOutOfStock || variantOutOfStock ? (
              <button
                className={`btn btn-disabled ml-[17px] flex h-[50px] min-w-[132px] items-center justify-center px-6! py-2! text-center lg:min-w-[240px]`}
                onClick={handleClick}
              >
                Out of stock
              </button>
            ) : (
              <button
                className={`btn btn-primary ml-[17px] flex h-[50px] min-w-[132px] items-center justify-center px-6! py-2! text-center lg:min-w-[240px] ${buttonColor}`}
                onClick={handleClick}
              >
                {buttonText}
              </button>
            )}
          </div>
        </div>
      </section>
    );
  }
}
